/** @jsxImportSource @emotion/react */


import { useState, useRef, useEffect } from 'react';

import photo_library from '@icons/photo_library.svg';

import { Button, Modal } from 'antd';
import OccSkeletonImage from '../common/OccSkeletonImage';

import icon_arrow_prev from '@icons/icon_arrow_40_b.svg';
import icon_arrow_next from '@icons/icon_arrow_40_f.svg';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router';
import { downloadFile, downloadImagesAsZip } from '@app/utils/file';
import { useTranslation } from 'react-i18next';
import { isLooseEqual } from '@app/utils/equality';


export default function ChattingImagesViewer({
  id,
  seq,
  detailTitle = '',
  images = [],
  max = 9,
  wrapStyle = {},
  borderRadius = '0px',
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const [page, setPage] = useState(0);
  const [thumbnailCount, setThumbnailCount] = useState(0);
  const sliderRef = useRef(null);

  useEffect(() => {
    setThumbnailCount(images.length < max ? images.length : max > 9 ? 9 : max);
  }, [images, max]);

  useEffect(() => {
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(page);
    }
  }, [page]);

  return (
    <>
      <div
        css={{ ...styles.wrap, ...wrapStyle }}
        className="flex-row align-stretch justify-stretch gap-4 wrap"
        style={{ flex: '1', cursor: 'pointer' }}
        onClick={() => {
          if (isMobile) {
            navigate(`/chat/${id}/seq/${seq}`);
          } else {
            setOpen(true);
          }
        }}
      >
        {isLooseEqual(thumbnailCount, 0) && (
          <div
            className="flex-row align-stretch justify-stretch"
            style={{ flex: 1, maxHeight: '360px' }}
          >
            <OccSkeletonImage
              image={null}
              active={false}
              borderRadius={borderRadius}
            />
          </div>
        )}
        {isLooseEqual(thumbnailCount, 1) && (
          <div
            className="flex-row align-stretch justify-stretch"
            style={{ flex: 1 }}
          >
            <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
          </div>
        )}

        {isLooseEqual(thumbnailCount, 2) && (
          <>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ flex: 1, height: 'auto' }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ flex: 1, height: 'auto' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
          </>
        )}
        {isLooseEqual(thumbnailCount, 3) && (
          <>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ flex: 1 }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ flex: 1 }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ flex: 1 }}
            >
              <OccSkeletonImage image={images[2]} borderRadius={borderRadius} />
            </div>
          </>
        )}
        {isLooseEqual(thumbnailCount, 4) && (
          <>
            <div
              className="flex-row align-stretch justify-stretch gap-4 wrap"
              style={{ flex: 1, overflow: 'hidden' }}
            >
              <div
                style={{
                  width: 'calc((100% - 4px) / 2)',
                  height: 'calc((100% - 4px) / 2)',
                }}
              >
                <OccSkeletonImage
                  image={images[0]}
                  borderRadius={borderRadius}
                />
              </div>
              <div
                style={{
                  width: 'calc((100% - 4px) / 2)',
                  height: 'calc((100% - 4px) / 2)',
                }}
              >
                <OccSkeletonImage
                  image={images[1]}
                  borderRadius={borderRadius}
                />
              </div>
              <div
                style={{
                  width: 'calc((100% - 4px) / 2)',
                  height: 'calc((100% - 4px) / 2)',
                }}
              >
                <OccSkeletonImage
                  image={images[2]}
                  borderRadius={borderRadius}
                />
              </div>
              <div
                style={{
                  width: 'calc((100% - 4px) / 2)',
                  height: 'calc((100% - 4px) / 2)',
                }}
              >
                <OccSkeletonImage
                  image={images[3]}
                  borderRadius={borderRadius}
                />
              </div>
            </div>
          </>
        )}
        {isLooseEqual(thumbnailCount, 5) && (
          <>
            <div
              className="flex-row align-stretch justify-stretch gap-4 "
              style={{ width: '100%', overflow: 'hidden' }}
            >
              <div
                style={{
                  width: 'calc((100% - 4px) / 2)',
                }}
              >
                <OccSkeletonImage
                  image={images[0]}
                  borderRadius={borderRadius}
                />
              </div>
              <div
                style={{
                  width: 'calc((100% - 4px) / 2)',
                }}
              >
                <OccSkeletonImage
                  image={images[1]}
                  borderRadius={borderRadius}
                />
              </div>
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ flex: 1 }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ flex: 1 }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ flex: 1 }}
            >
              <OccSkeletonImage image={images[2]} borderRadius={borderRadius} />
            </div>
          </>
        )}
        {isLooseEqual(thumbnailCount, 6) && (
          <>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[2]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[3]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[4]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[5]} borderRadius={borderRadius} />
            </div>
          </>
        )}
        {isLooseEqual(thumbnailCount, 7) && (
          <>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 4px) / 2)' }}
            >
              <OccSkeletonImage image={images[2]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 4px) / 2)' }}
            >
              <OccSkeletonImage image={images[3]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 4px) / 2)' }}
            >
              <OccSkeletonImage image={images[4]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 4px) / 2)' }}
            >
              <OccSkeletonImage image={images[5]} borderRadius={borderRadius} />
            </div>
          </>
        )}
        {isLooseEqual(thumbnailCount, 8) && (
          <>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 4px) / 2)' }}
            >
              <OccSkeletonImage image={images[4]} borderRadius={borderRadius} />
            </div>

            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 4px) / 2)' }}
            >
              <OccSkeletonImage image={images[5]} borderRadius={borderRadius} />
            </div>
          </>
        )}
        {isLooseEqual(thumbnailCount, 9) && (
          <>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[0]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
            <div
              className="flex-row align-stretch justify-stretch"
              style={{ width: 'calc((100% - 8px) / 3)' }}
            >
              <OccSkeletonImage image={images[1]} borderRadius={borderRadius} />
            </div>
          </>
        )}
        {images.length > thumbnailCount && (
          <div style={{ position: 'absolute', right: '12px', bottom: '12px' }}>
            <Button
              shape="round"
              block
              disabled={false}
              loading={false}
              style={{
                border: 'none',
                height: '28px',
                backgroundColor: '#00000080',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '10px',
                paddingRight: '10px',
                gap: '4px',
              }}
            >
              <img alt=""
                src={photo_library}
                style={{ width: '16px', height: '16px', display: 'block' }}
              />
              <span
                style={{
                  color: '#FFF',
                  fontSize: '16px',
                  lineHeight: '16px',
                  display: 'block',
                }}
              >
                {images.length - thumbnailCount}+
              </span>
            </Button>
          </div>
        )}
      </div>
      <Modal
        open={open}
        onCancel={() => {
          setOpen(false);
        }}
        title={
          <div className="flex gap-16 align-center">
            <span style={{ fontSize: '16px', fontWeight: 600 }}>
              {detailTitle}
            </span>
            <div>
              <Button
                shape="round"
                block
                disabled={false}
                onClick={() => {
                  downloadImagesAsZip(images);
                }}
                loading={false}
                style={{
                  borderColor: '#000',
                  height: '30px',
                  color: '#000',
                  fontWeight: 400,
                  minWidth: '124px',
                }}
              >
                {t('사진 전체 저장')}
              </Button>
            </div>
          </div>
        }
        styles={{
          content: {
            padding: '20px 26px',
            height: '80vh',
            overflow: 'hidden',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          },
          body: { flex: 1, minHeight: 0, overflow: 'hidden' },
          header: { marginBottom: '16px' },
          footer: {
            marginTop: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        style={{ width: '80%', maxWidth: '1182px' }}
        width={'80%'}
        footer={null}
      >
        <div
          className="flex wrap gap-10 align-start justify-start"
          style={{
            height: '100%',
            width: '100%',
            alignContent: 'flex-start',
            overflowY: 'auto',
          }}
        >
          {images.map((image, i) => {
            return (
              <div
                style={{
                  width: 'calc((100% - 20px) / 3)',
                  height: 'calc((100% - 20px) / 3)',
                  cursor: 'pointer',
                }}
                key={image}
                onClick={() => {
                  setPage(i);
                  setDetailOpen(true);
                }}
              >
                <OccSkeletonImage image={image} />
              </div>
            );
          })}
        </div>
      </Modal>
      <Modal
        open={detailOpen}
        onCancel={() => {
          setOpen(false);
          setDetailOpen(false);
        }}
        title={
          <div className="flex gap-16 align-center">
            <div>
              <Button
                shape="round"
                block
                disabled={false}
                onClick={() => {
                  setDetailOpen(false);
                }}
                loading={false}
                style={{
                  borderColor: '#000',
                  height: '30px',
                  color: '#000',
                  fontWeight: 400,
                  minWidth: '79px',
                }}
              >
                {t('갤러리')}
              </Button>
            </div>
            <div>
              <Button
                shape="round"
                block
                disabled={false}
                onClick={() => {
                  downloadFile(images[page]);
                }}
                loading={false}
                style={{
                  borderColor: '#000',
                  height: '30px',
                  color: '#000',
                  fontWeight: 400,
                  minWidth: '124px',
                }}
              >
                {t('이 사진만 저장')}
              </Button>
            </div>
          </div>
        }
        styles={{
          content: {
            padding: '17px 20px 30px 20px',
            height: '80vh',
            overflow: 'hidden',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
          },
          body: { flex: 1, minHeight: 0, overflow: 'hidden' },
          header: { marginBottom: '41px' },
          footer: {
            marginTop: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
        }}
        style={{ width: '80%', maxWidth: '1182px' }}
        width={'80%'}
        footer={
          <div>
            <span style={{ fontSize: '14px', fontWeight: 400 }}>
              <span style={{ fontWeight: 600 }}>{page + 1}</span>/
              {images.length}
            </span>
          </div>
        }
      >
        <div css={styles.slider}>
          <Button
            type="text"
            icon={<img alt={t('common:imageAlt.previous')} src={icon_arrow_prev} />}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '40px',
              height: '40px',
            }}
            onClick={() => {
              sliderRef.current.slickPrev();
            }}
          />

          <div className="slider-wrap">
            <Slider
              ref={sliderRef}
              {...settings}
              style={{ height: '100%' }}
              afterChange={(page) => setPage(page)}
            >
              {images.map((image, i) => {
                return (
                  <OccSkeletonImage
                    image={image}
                    key={image}
                    objectFit="contain"
                  />
                );
              })}
            </Slider>
          </div>
          <Button
            type="text"
            icon={<img alt={t('common:imageAlt.next')} src={icon_arrow_next} />}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '40px',
              height: '40px',
            }}
            onClick={() => {
              sliderRef.current.slickNext();
            }}
          />
        </div>
      </Modal>
    </>
  );
}

const styles = {
  wrap: {
    borderRadius: '4px',
    overflow: 'hidden',
    position: 'relative',
    height: '100%',
    '& .ant-skeleton-element': {
      width: '100%',
      height: '100%',
    },
  },
  slider: {
    display: 'flex !important',
    flexDirection: 'row !important',
    height: '100%',
    alignItems: 'center',
    width: '100%',
    '& .slider-wrap': {
      flex: 1,
      height: '100%',
      overflow: 'hidden',
    },
    '& .slick-slider': { height: '100%' },
    '& .slick-list': { height: '100%' },
    '& .slick-track': { height: '100%' },
    '& .slick-slide > div': { height: '100%' },
  },
};
var settings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  centerMode: false,
  swipe: true,
};
